import { getApiBaseUrl } from '@/lib/config';

export type ConnectivityOutcome =
  | 'ok'
  | 'http-error'
  | 'network-error'
  | 'timeout';

export interface ConnectivityResult {
  reachable: boolean;
  outcome: ConnectivityOutcome;
  httpStatus: number | null;
  latencyMs: number;
  online: boolean;
  servedOrigin: string;
  apiBaseUrl: string;
  // null is reserved; probeSession always returns a number or 'network-error'
  sessionStatus: number | 'network-error' | null;
}

const HEALTH_PATH = '/health/';
const HEALTH_READY_PATH = '/health/ready/';
const SESSION_PATH = '/_allauth/browser/v1/auth/session';
const DEFAULT_TIMEOUT_MS = 5000;

function nowMs(): number {
  try {
    return performance.now();
  } catch {
    return 0;
  }
}

function getOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

function getServedOrigin(): string {
  return typeof window !== 'undefined' && window.location
    ? window.location.origin
    : '';
}

// Absolute probe URL. MUST never throw: getApiBaseUrl() throws when
// VITE_APP_BASE_URL is undefined (config.ts:16-22), and new URL(...) can throw.
function toAbsoluteUrl(path: string): string {
  let base = '';
  try {
    base = getApiBaseUrl();
  } catch {
    base = '';
  }
  const target = `${base}${path}`;
  try {
    const origin = getServedOrigin() || undefined;
    return new URL(target, origin).href;
  } catch {
    return target;
  }
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

interface ReachabilityProbe {
  outcome: ConnectivityOutcome;
  httpStatus: number | null;
  latencyMs: number;
}

async function probeReachability(
  url: string,
  timeoutMs: number
): Promise<ReachabilityProbe> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const start = nowMs();
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
      signal: controller.signal,
    });
    return {
      outcome: response.ok ? 'ok' : 'http-error',
      httpStatus: response.status,
      latencyMs: Math.round(nowMs() - start),
    };
  } catch (error) {
    return {
      outcome: isAbortError(error) ? 'timeout' : 'network-error',
      httpStatus: null,
      latencyMs: Math.round(nowMs() - start),
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

interface ReachabilityResult extends ReachabilityProbe {
  apiBaseUrl: string;
}

async function checkReachability(
  timeoutMs: number
): Promise<ReachabilityResult> {
  const primaryUrl = toAbsoluteUrl(HEALTH_PATH);
  const primary = await probeReachability(primaryUrl, timeoutMs);
  // Fall back to /health/ready/ ONLY on http-error (a response arrived but
  // non-2xx). Do NOT retry on network-error/timeout — the same host is
  // unreachable, so a retry only doubles the wait.
  if (primary.outcome === 'http-error') {
    const fallbackUrl = toAbsoluteUrl(HEALTH_READY_PATH);
    const fallback = await probeReachability(fallbackUrl, timeoutMs);
    if (fallback.outcome === 'ok') {
      return { ...fallback, apiBaseUrl: fallbackUrl };
    }
  }
  return { ...primary, apiBaseUrl: primaryUrl };
}

async function probeSession(
  timeoutMs: number
): Promise<number | 'network-error'> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(toAbsoluteUrl(SESSION_PATH), {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
      cache: 'no-store',
      signal: controller.signal,
    });
    return response.status;
  } catch {
    // Any failure — network error OR timeout/abort — reported as 'network-error'.
    return 'network-error';
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function runConnectivityCheck(
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<ConnectivityResult> {
  // Capture environment BEFORE any async work using throw-proof helpers, so the
  // catch below never has to re-read globals (which could themselves throw).
  const online = getOnline();
  const servedOrigin = getServedOrigin();
  try {
    const [reachability, sessionStatus] = await Promise.all([
      checkReachability(timeoutMs),
      probeSession(timeoutMs),
    ]);
    return {
      reachable: reachability.outcome === 'ok',
      outcome: reachability.outcome,
      httpStatus: reachability.httpStatus,
      latencyMs: reachability.latencyMs,
      online,
      servedOrigin,
      apiBaseUrl: reachability.apiBaseUrl,
      sessionStatus,
    };
  } catch {
    // Defense-in-depth: the probes above are individually throw-proof, so this
    // should be unreachable. If it ever fires, RESOLVE (never reject) with a
    // fully-populated network-error result built only from captured values and
    // the throw-proof toAbsoluteUrl.
    return {
      reachable: false,
      outcome: 'network-error',
      httpStatus: null,
      latencyMs: 0,
      online,
      servedOrigin,
      apiBaseUrl: toAbsoluteUrl(HEALTH_PATH),
      sessionStatus: 'network-error',
    };
  }
}
